/**
 * Alertas (§7).
 *
 * Tres límites que este archivo respeta y que no hay que aflojar:
 *
 * 1. **La app no diagnostica.** Ante dolor recurrente se recomienda consultar a un
 *    profesional, sin ninguna interpretación clínica. No hay una sola alerta acá que diga
 *    qué tenés; dicen qué se observó en los datos.
 * 2. **Nada de composición corporal se alerta con menos de 3 lecturas comparables** (§5.3).
 *    Una balanza de bioimpedancia con dos lecturas no sostiene ninguna conclusión.
 * 3. **Ninguna alerta reta por falta de pasado.** Una ausencia sólo se señala cuando hubo
 *    historial suficiente para que la ausencia signifique algo; y si el plan ya trae lo que
 *    falta, la alerta lo dice en vez de pedirlo.
 *
 * Y por §10: nada de esto habilita notificaciones culpabilizantes. El tono es informar,
 * no retar.
 */

import type { PlanCalendario } from './calendario.ts';
import { UMBRAL_COLCHON_CRITICO } from './calendario.ts';
import { NOMBRE_GRUPO, cargaMuscular, grupoDe } from './musculos.ts';
import {
  MIN_LECTURAS_COMPARABLES,
  UMBRAL_PRATIO_BAJO,
  LIMITE_PERDIDA_SEMANAL_PCT,
  condicionesMixtas,
  masaMagraEnDescenso,
  pRatio,
  velocidadPerdida,
} from './kpis/cuerpo.ts';
import { detectarEstancamiento, omisionesConsecutivas } from './kpis/fuerza.ts';
import { UMBRAL_ESTIMULO, gruposAbandonados, historialParaAbandono } from './kpis/musculo.ts';
import {
  diasBajoObjetivoProteina,
  promedio as promedioNutricion,
  type Alimento,
  type ObjetivosNutricion,
} from './kpis/nutricion.ts';
import type {
  Ejercicio,
  Fecha,
  GrupoMuscular,
  MedidaCorporal,
  SerieContexto,
  Sexo,
} from './types.ts';

export type TipoAlerta =
  | 'proteina_bajo_objetivo'
  | 'estancamiento'
  | 'perdida_masa_magra'
  | 'calidad_perdida_baja'
  | 'deficit_agresivo'
  | 'colchon_critico'
  | 'sesiones_en_riesgo'
  | 'musculo_abandonado'
  | 'dolor_recurrente';

export type SeveridadAlerta = 'info' | 'atencion' | 'importante';

export interface Alerta {
  tipo: TipoAlerta;
  severidad: SeveridadAlerta;
  titulo: string;
  detalle: string;
  /** Qué se puede hacer. Nunca un diagnóstico. */
  accion: string | null;
  /** true cuando el tema es de salud y corresponde recordar que la app no diagnostica. */
  derivarProfesional: boolean;
  /** Identificador estable para poder silenciar una alerta puntual sin silenciar el tipo. */
  clave: string;
}

export const AVISO_NO_DIAGNOSTICA =
  'Esto es una observación sobre tus registros, no un diagnóstico. Si el dolor persiste, ' +
  'consultá a un profesional de la salud.';

export interface EntradaAlertas {
  hoy: Fecha;
  series: SerieContexto[];
  ejercicios: Ejercicio[];
  medidas: MedidaCorporal[];
  alturaCm: number | null;
  sexo: Sexo;
  calendario?: PlanCalendario | null;
  /** Ventana para el p-ratio: §7 la fija en 4 semanas. */
  ventanaPRatioDias?: number;
  /** Comidas registradas. Opcional: sin ellas no se emite ninguna alerta de nutrición. */
  alimentos?: Alimento[];
  /**
   * Objetivos que el usuario escribió. §10 prohíbe que la app prescriba números de calorías, así
   * que sin objetivo propio no hay nada que comparar y no se alerta.
   */
  objetivosNutricion?: ObjetivosNutricion;
  /**
   * El próximo día del plan, si hay plan. **Opcional**: sin esto nada cambia.
   *
   * Con esto, la alerta de músculo abandonado deja de pedir lo que el calendario ya trae. Pedir
   * "metelo en la próxima sesión" cuando la próxima sesión ES el día de piernas no es un aviso:
   * es la app hablando sin mirar su propio plan.
   *
   * Van los ejercicios y no los músculos ya resueltos porque el mapeo ejercicio → grupo es un
   * cálculo, y los cálculos viven en core: si lo resolviera la web habría dos taxonomías.
   */
  proximaSesion?: { nombre: string; ejercicios: Ejercicio[] } | null;
}

/**
 * Los grupos que un día de rutina entrena **de verdad**.
 *
 * Se exige carga primaria, el mismo umbral que usa el mapa de calor para dar un grupo por
 * estimulado. Aceptar secundarios haría que el back extension del Día 2 —glúteos e isquios de
 * acompañamiento— alcance para decir "la próxima sesión ya trae pierna", y la alerta quedaría
 * callada por un trabajo colateral que no es entrenar pierna.
 */
function gruposQueTrae(ejercicios: Ejercicio[]): Set<GrupoMuscular> {
  const out = new Set<GrupoMuscular>();
  for (const ej of ejercicios) {
    for (const [m, carga] of cargaMuscular(ej)) {
      if (carga < UMBRAL_ESTIMULO) continue;
      const g = grupoDe(m);
      if (g) out.add(g);
    }
  }
  return out;
}

/**
 * Qué mirar cuando la calidad de la pérdida es baja. Si hay proteína registrada, se le devuelve su
 * propio dato en vez de un consejo genérico — sigue siendo descripción, no prescripción.
 */
function accionCalidadBaja(e: EntradaAlertas): string {
  const base = 'Revisá tres cosas en este orden: proteína, tamaño del déficit y estímulo de fuerza.';
  const alimentos = e.alimentos ?? [];
  if (alimentos.length === 0) return `${base} Si registrás lo que comés, la app puede mostrarte cuánta proteína estuviste comiendo.`;

  const prom = promedioNutricion(alimentos, e.hoy, 14);
  if (prom.proteina_g === null) return base;
  return `${base} En los ${prom.diasConRegistro} días que anotaste promediaste ${Math.round(prom.proteina_g)} g de proteína por día.`;
}

export function evaluarAlertas(e: EntradaAlertas): Alerta[] {
  const alertas: Alerta[] = [];
  const ventanaPRatio = e.ventanaPRatioDias ?? 28;

  // --- Estancamiento: 3 sesiones iguales en un ejercicio ---------------------
  const ejerciciosConSeries = new Set(e.series.map((c) => c.serie.ejercicio_id));
  for (const ej of e.ejercicios) {
    if (!ejerciciosConSeries.has(ej.id)) continue;
    const est = detectarEstancamiento(e.series, ej.id, 3);
    if (!est) continue;
    alertas.push({
      tipo: 'estancamiento',
      severidad: 'atencion',
      titulo: `${ej.nombre} estancado`,
      detalle: `${est.sesiones} sesiones con el mismo peso y las mismas reps (${est.desde} → ${est.hasta}).`,
      accion: 'Probá sumar una serie o subir reps antes de insistir con el mismo peso.',
      derivarProfesional: false,
      clave: `estancamiento:${ej.id}:${est.hasta}`,
    });
  }

  // --- Dolor recurrente: mismo ejercicio omitido por dolor 2 veces -----------
  for (const ej of e.ejercicios) {
    if (!ejerciciosConSeries.has(ej.id)) continue;
    const veces = omisionesConsecutivas(e.series, ej.id, 'dolor');
    if (veces < 2) continue;
    alertas.push({
      tipo: 'dolor_recurrente',
      severidad: 'importante',
      titulo: `${ej.nombre}: dolor ${veces} veces seguidas`,
      detalle: `Marcaste ${ej.nombre} como omitido por dolor en las últimas ${veces} sesiones en que aparecía.`,
      accion: AVISO_NO_DIAGNOSTICA,
      derivarProfesional: true,
      clave: `dolor:${ej.id}:${veces}`,
    });
  }

  // --- Músculo abandonado: grupo sin estímulo en 14 días ---------------------
  //
  // Dos frenos, los dos por §10 ("nada de gamificación que castigue"):
  //
  // 1. **Historial mínimo.** Sin él, la primera sesión de la vida disparaba "Espalda sin
  //    estímulo" y "Hombros sin estímulo" por músculos que era imposible haber entrenado
  //    todavía. Una ausencia sólo significa algo cuando hubo oportunidad de que no ocurriera:
  //    2 sesiones y 7 días, ver `historialParaAbandono`.
  // 2. **Si el plan ya lo trae, se dice; no se pide.** Ver `proximaSesion`.
  const historial = historialParaAbandono(e.series, e.hoy);
  if (historial.suficiente) {
    const traeLaProxima = e.proximaSesion ? gruposQueTrae(e.proximaSesion.ejercicios) : null;

    for (const g of gruposAbandonados(e.series, e.hoy)) {
      const yaLoTrae = traeLaProxima?.has(g.grupo) ?? false;
      const desde =
        g.ultimoEstimulo === null
          ? `Todavía no hay ninguna serie registrada para ${NOMBRE_GRUPO[g.grupo].toLowerCase()}.`
          : `${g.diasSinEstimulo} días desde la última vez (${g.ultimoEstimulo}).`;

      alertas.push({
        tipo: 'musculo_abandonado',
        // Si la próxima sesión ya lo trae no hay nada que hacer: es un dato, no algo que atender.
        severidad: yaLoTrae ? 'info' : 'atencion',
        titulo: `${NOMBRE_GRUPO[g.grupo]} sin estímulo`,
        detalle: yaLoTrae
          ? `${desde} La próxima sesión (${e.proximaSesion!.nombre}) ya lo trae.`
          : desde,
        accion: yaLoTrae ? null : 'Metelo en la próxima sesión, aunque sea un ejercicio.',
        derivarProfesional: false,
        clave: `abandonado:${g.grupo}:${g.ultimoEstimulo ?? 'nunca'}`,
      });
    }
  }

  // --- Calendario ------------------------------------------------------------
  if (e.calendario) {
    const cal = e.calendario;
    if (cal.colchon < UMBRAL_COLCHON_CRITICO) {
      alertas.push({
        tipo: 'colchon_critico',
        severidad: cal.colchon < 0 ? 'importante' : 'atencion',
        titulo: cal.colchon < 0 ? 'No alcanzan los días' : `Colchón de ${cal.colchon} ${cal.colchon === 1 ? 'día' : 'días'}`,
        detalle: `${cal.diasHabiles} días hábiles hasta el vencimiento para ${cal.sesionesPendientes} sesiones pendientes.`,
        accion:
          cal.colchon < 0
            ? 'Hablá con el gimnasio para extender el plan, o agregá días si el gimnasio abre otros horarios.'
            : 'Cualquier falta te deja sin margen: si podés, adelantá una sesión.',
        derivarProfesional: false,
        clave: `colchon:${cal.hasta}:${cal.colchon}`,
      });
    }
    if (cal.sesionesEnRiesgo > 0) {
      alertas.push({
        tipo: 'sesiones_en_riesgo',
        severidad: 'importante',
        titulo: `${cal.sesionesEnRiesgo} ${cal.sesionesEnRiesgo === 1 ? 'sesión' : 'sesiones'} en riesgo de vencer`,
        detalle: `Con los días disponibles hasta el ${cal.hasta}, ${cal.sesionesEnRiesgo} ${cal.sesionesEnRiesgo === 1 ? 'sesión va' : 'sesiones van'} a sobrar.`,
        accion: 'Pedí extensión del plan o reprogramá los días bloqueados si alguno se puede recuperar.',
        derivarProfesional: false,
        clave: `riesgo:${cal.hasta}:${cal.sesionesEnRiesgo}`,
      });
    }
  }

  // --- Composición corporal -------------------------------------------------
  // §5.3: nada de esto se emite con menos de 3 lecturas comparables.
  if (e.medidas.length >= MIN_LECTURAS_COMPARABLES) {
    const mixtas = condicionesMixtas(e.medidas);
    const sufijoCondiciones = mixtas
      ? ' Ojo: hay lecturas tomadas en condiciones distintas (ayuno vs. post-entreno), y eso solo ya mueve el número.'
      : '';

    const pr = pRatio(e.medidas, e.alturaCm, { ventanaDias: ventanaPRatio, hasta: e.hoy });
    if (pr.estado === 'ok' && pr.calidad !== null && pr.calidad < UMBRAL_PRATIO_BAJO) {
      alertas.push({
        tipo: 'calidad_perdida_baja',
        severidad: 'importante',
        titulo: `Calidad de la pérdida en ${Math.round(pr.calidad)} %`,
        detalle: `${pr.detalle} Por debajo del ${UMBRAL_PRATIO_BAJO} % se está perdiendo demasiada masa magra.${sufijoCondiciones}`,
        accion: accionCalidadBaja(e),
        derivarProfesional: false,
        clave: `pratio:${pr.hasta}:${Math.round(pr.calidad)}`,
      });
    }

    const magra = masaMagraEnDescenso(e.medidas, e.alturaCm, MIN_LECTURAS_COMPARABLES);
    if (magra.enDescenso && magra.suficiente) {
      const primero = magra.valores[0]!;
      const ultimo = magra.valores[magra.valores.length - 1]!;
      alertas.push({
        tipo: 'perdida_masa_magra',
        severidad: 'importante',
        titulo: 'Masa magra en descenso',
        detalle: `Bajó en las últimas ${magra.lecturasSeguidas} lecturas: ${primero.media.toFixed(1)} → ${ultimo.media.toFixed(1)} kg (media móvil).${sufijoCondiciones}`,
        accion: 'Es lo que hay que mantener plano mientras el peso baja. Subí proteína y no recortes más el déficit.',
        derivarProfesional: false,
        clave: `magra:${ultimo.fecha}`,
      });
    }

    const vel = velocidadPerdida(e.medidas, { ventanaDias: ventanaPRatio, hasta: e.hoy });
    if (vel.estado === 'ok' && vel.excedeBanda && vel.porcentajeSemanal !== null) {
      alertas.push({
        tipo: 'deficit_agresivo',
        severidad: 'atencion',
        titulo: `Bajando ${vel.porcentajeSemanal.toFixed(1)} % por semana`,
        detalle: `Por encima de ${LIMITE_PERDIDA_SEMANAL_PCT} % del peso corporal por semana sostenido, sube el riesgo de perder masa magra.${sufijoCondiciones}`,
        accion: 'Aflojá un poco el déficit: bajar más lento conserva más músculo.',
        derivarProfesional: false,
        clave: `velocidad:${e.hoy}:${vel.porcentajeSemanal.toFixed(1)}`,
      });
    }
  }

  // --- Proteína por debajo del objetivo PROPIO --------------------------------
  // Sólo existe si el usuario escribió un objetivo. La app no inventa uno ni recomienda comer algo.
  const objetivoProteina = e.objetivosNutricion?.proteina_g ?? null;
  const alimentos = e.alimentos ?? [];

  if (objetivoProteina && objetivoProteina > 0 && alimentos.length > 0) {
    const dias = diasBajoObjetivoProteina(alimentos, objetivoProteina, e.hoy);
    if (dias >= 3) {
      const prom = promedioNutricion(alimentos, e.hoy, 7);
      alertas.push({
        tipo: 'proteina_bajo_objetivo',
        severidad: 'atencion',
        titulo: `${dias} días seguidos bajo tu objetivo de proteína`,
        detalle:
          prom.proteina_g === null
            ? `Tu objetivo son ${objetivoProteina} g por día.`
            : `Promediaste ${Math.round(prom.proteina_g)} g en los ${prom.diasConRegistro} días que anotaste, contra tu objetivo de ${objetivoProteina} g.`,
        // El objetivo lo puso el usuario, así que se le devuelve su propio número. Nada de qué comer.
        accion: 'Es tu propio objetivo, no una recomendación de la app.',
        derivarProfesional: false,
        clave: `proteina:${e.hoy}:${dias}`,
      });
    }
  }

  const orden: Record<SeveridadAlerta, number> = { importante: 0, atencion: 1, info: 2 };
  return alertas.sort((a, b) => orden[a.severidad] - orden[b.severidad]);
}
